import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse, HttpStatusCode } from 'axios';
import { ApiUrlUtil } from 'app/utils/apiUrlUtil';
import { HeadersUtil } from 'app/utils/headersUtil';
import {
  ChatMessage,
  ChatSession,
  InventorySuggestion,
  mergeServerMessages,
  sortChatMessages,
} from 'app/model/ai/chat';
import { ResponseApi, ResponseResultCode } from 'app/model/response-api';

/** nginx /ai-api URL (Gateway 미사용). REACT_APP_URL_AI 필수. */
const AI_BASE_URL = process.env['REACT_APP_URL_AI'] ?? 'https://eneruse.a2m.co.kr/ai-api';

async function requestAiApi<T>(method: 'get' | 'post' | 'delete', path: string, body?: unknown): Promise<T | null> {
  const url = ApiUrlUtil.buildQueryString(`${AI_BASE_URL}${path}`);
  const config = { headers: HeadersUtil.getHeadersAuth() };
  let response: AxiosResponse;

  if (method === 'get') {
    response = await axios.get(url, config);
  } else if (method === 'post') {
    response = await axios.post(url, body, config);
  } else {
    response = await axios.delete(url, config);
  }

  if (response?.status === HttpStatusCode.Ok) {
    const apiResponse: ResponseApi<T> = response.data;
    if (apiResponse?.resultCode === ResponseResultCode.Y) {
      return apiResponse.data;
    }
  }
  return null;
}

interface AiChatState {
  isOpen: boolean;
  sessions: ChatSession[];
  activeSessionId: string | null;
  messages: ChatMessage[];
  streaming: boolean;
  streamingContent: string;
  loading: boolean;
  healthChecking: boolean;
  serviceAvailable: boolean;
  streamError: string | null;
  responseReadyNotice: boolean;
  pendingImage: string | null;
  pendingInventory: InventorySuggestion | null;
}

const initialState: AiChatState = {
  isOpen: false,
  sessions: [],
  activeSessionId: null,
  messages: [],
  streaming: false,
  streamingContent: '',
  loading: false,
  healthChecking: false,
  serviceAvailable: false,
  streamError: null,
  responseReadyNotice: false,
  pendingImage: null,
  pendingInventory: null,
};

export const checkAiHealth = createAsyncThunk('aiChat/checkHealth', async () => {
  try {
    const url = ApiUrlUtil.buildQueryString(`${AI_BASE_URL}/health`);
    const response = await axios.get(url, {
      timeout: 8000,
      validateStatus: () => true,
    });
    return response.data?.status === 'ok';
  } catch {
    return false;
  }
});

export const fetchSessions = createAsyncThunk('aiChat/fetchSessions', async () => {
  return (await requestAiApi<ChatSession[]>('get', '/sessions')) ?? [];
});

export const createSession = createAsyncThunk('aiChat/createSession', async (title?: string) => {
  return requestAiApi<ChatSession>('post', '/sessions', { title });
});

export const deleteSession = createAsyncThunk('aiChat/deleteSession', async (sessionId: string) => {
  const deleted = (await requestAiApi<boolean>('delete', `/sessions/${sessionId}`)) ?? false;
  return { sessionId, deleted };
});

export const fetchMessages = createAsyncThunk('aiChat/fetchMessages', async (sessionId: string) => {
  const messages = (await requestAiApi<ChatMessage[]>('get', `/sessions/${sessionId}/messages`)) ?? [];
  return { sessionId, messages };
});

const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    toggleChat: (state) => {
      state.isOpen = !state.isOpen;
      if (state.isOpen) {
        state.serviceAvailable = false;
        state.streamError = null;
        state.responseReadyNotice = false;
      }
    },
    closeChat: (state) => {
      state.isOpen = false;
    },
    setResponseReadyNotice: (state, action: PayloadAction<boolean>) => {
      state.responseReadyNotice = action.payload;
    },
    setActiveSessionId: (state, action: PayloadAction<string | null>) => {
      state.activeSessionId = action.payload;
    },
    startStreaming: (state) => {
      state.streaming = true;
      state.streamingContent = '';
      state.streamError = null;
    },
    appendStreamToken: (state, action: PayloadAction<string>) => {
      state.streamingContent += action.payload;
    },
    completeStreaming: (state, action: PayloadAction<string>) => {
      const assistantContent = state.streamingContent.trim();
      if (assistantContent) {
        state.messages.push({
          messageId: `temp-assistant-${Date.now()}`,
          sessionId: action.payload,
          role: 'assistant',
          content: assistantContent,
          createdAt: new Date().toISOString(),
        });
      }
      state.streaming = false;
      state.streamingContent = '';
      state.activeSessionId = action.payload;
      state.streamError = null;
      state.messages = sortChatMessages(state.messages);
    },
    addUserMessage: (state, action: PayloadAction<{ sessionId: string; content: string; imageUrl?: string }>) => {
      state.messages.push({
        messageId: `temp-user-${Date.now()}`,
        sessionId: action.payload.sessionId,
        role: 'user',
        content: action.payload.content,
        createdAt: new Date().toISOString(),
        imageUrl: action.payload.imageUrl,
      });
      state.messages = sortChatMessages(state.messages);
    },
    resetStreaming: (state) => {
      state.streaming = false;
      state.streamingContent = '';
    },
    setStreamError: (state, action: PayloadAction<string | null>) => {
      state.streamError = action.payload;
      state.streaming = false;
      state.streamingContent = '';
    },
    setPendingImage: (state, action: PayloadAction<string | null>) => {
      state.pendingImage = action.payload;
    },
    clearPendingImage: (state) => {
      state.pendingImage = null;
    },
    setPendingInventory: (state, action: PayloadAction<InventorySuggestion | null>) => {
      state.pendingInventory = action.payload;
    },
    clearPendingInventory: (state) => {
      state.pendingInventory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload;
      })
      .addCase(fetchSessions.rejected, (state) => {
        state.loading = false;
        state.sessions = [];
      })
      .addCase(createSession.fulfilled, (state, action) => {
        if (action.payload) {
          state.sessions = [action.payload, ...state.sessions].slice(0, 10);
          state.activeSessionId = action.payload.sessionId;
          state.messages = [];
        }
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter(
          (session) => session.sessionId !== action.payload.sessionId,
        );
        if (state.activeSessionId === action.payload.sessionId) {
          state.activeSessionId = state.sessions[0]?.sessionId ?? null;
          state.messages = [];
        }
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        if (state.activeSessionId !== action.payload.sessionId) {
          return;
        }
        if (state.streaming) {
          return;
        }
        state.messages = mergeServerMessages(action.payload.messages, state.messages);
      })
      .addCase(checkAiHealth.pending, (state) => {
        state.healthChecking = true;
        state.serviceAvailable = false;
      })
      .addCase(checkAiHealth.fulfilled, (state, action) => {
        state.healthChecking = false;
        state.serviceAvailable = action.payload;
      })
      .addCase(checkAiHealth.rejected, (state) => {
        state.healthChecking = false;
        state.serviceAvailable = false;
      });
  },
});

export const {
  toggleChat,
  closeChat,
  setResponseReadyNotice,
  setActiveSessionId,
  startStreaming,
  appendStreamToken,
  completeStreaming,
  addUserMessage,
  resetStreaming,
  setStreamError,
  setPendingImage,
  clearPendingImage,
  setPendingInventory,
  clearPendingInventory,
} = aiChatSlice.actions;

export default aiChatSlice.reducer;
